<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  GG_SUPPORT_ATTRIBUTE_KEY,
  useGgSupportTickets,
} from 'dashboard/composables/useGgSupportTickets';
import { useMapGetter } from 'dashboard/composables/store';
import HelperTextPopup from 'dashboard/components/ui/HelperTextPopup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({ conversationId: { type: Number, required: true } });
const { t } = useI18n();
const attributesByModel = useMapGetter('attributes/getAttributesByModel');
const attributeDefinition = computed(() =>
  attributesByModel
    .value('conversation_attribute')
    .find(attribute => attribute.attribute_key === GG_SUPPORT_ATTRIBUTE_KEY)
);
const conversationId = computed(() => props.conversationId);
const { enabled, tickets, error, busy, load } =
  useGgSupportTickets(conversationId);
const titleId = computed(() => `gg-support-tickets-${conversationId.value}`);
</script>

<template>
  <section
    v-if="enabled && attributeDefinition"
    :aria-labelledby="titleId"
    class="border-t border-n-weak/50 px-4 py-3"
  >
    <h4
      :id="titleId"
      class="m-0 mb-1 inline-flex w-full items-start gap-1.5 whitespace-nowrap text-sm font-medium text-n-slate-12"
    >
      {{ attributeDefinition.attribute_display_name }}
      <HelperTextPopup
        v-if="attributeDefinition.attribute_description"
        :message="attributeDefinition.attribute_description"
        class="mt-0.5"
      />
    </h4>
    <Spinner v-if="busy" class="my-1" />
    <template v-else-if="error">
      <p role="alert" class="m-0 text-xs text-n-ruby-11">
        {{ t('GG_SUPPORT_HEADER.ERROR') }}
      </p>
      <Button
        :label="t('GG_SUPPORT_HEADER.RETRY')"
        color="slate"
        variant="ghost"
        size="xs"
        @click="load"
      />
    </template>
    <p v-else-if="!tickets.length" class="m-0 text-sm text-n-slate-11">
      {{ t('GG_SUPPORT_HEADER.EMPTY') }}
    </p>
    <div
      v-else
      role="list"
      class="flex max-h-40 flex-wrap gap-x-3 gap-y-1 overflow-y-auto text-sm text-n-slate-12"
    >
      <span
        v-for="ticket in tickets"
        :key="ticket.chamado_id"
        role="listitem"
        :title="t('GG_SUPPORT_HEADER.TICKET', { id: ticket.chamado_id })"
        class="whitespace-nowrap"
      >
        {{
          ticket.ticket_code ||
          t('GG_SUPPORT_HEADER.TICKET', { id: ticket.chamado_id })
        }}
      </span>
    </div>
  </section>
</template>
